import { VOICE_PARTS } from '@corocanto/scoring';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
  type AdminMembership,
  type AdminUser,
  type UserStatus,
  useAdminChoirs,
  useAdminStatus,
  useAdminUsers,
  useCreateUser,
  useStartViewAs,
  useUserChanges,
  useUserPanel,
} from '../../api/admin';
import type { VoicePart } from '../../api/types';
import { Person } from '../../components/Avatar';
import { Dialog } from '../../components/Dialog';
import { FilterChips } from '../../components/FilterChips';
import { ErrorView } from '../../components/Guards';
import { CopyButton } from '../../components/MemberDialogs';
import { dateLocale, LANGUAGES } from '../../i18n';
import { formatDate, formatDateTime } from '../../lib/format';
import { avatarKind } from '../../lib/people';
import ui from '../../styles/ui.module.css';
import { apiErrorText, relativeTime } from './adminFormat';
import styles from './AdminPages.module.css';

const STATUSES: UserStatus[] = ['ACTIVE', 'SETUP_PENDING', 'DEACTIVATED'];
const DAY_MS = 24 * 60 * 60 * 1000;

/** The administrator's users (wireframe 11): search, choir filter, status chips, and a user's panel. */
export function AdminUsersPage() {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [choirId, setChoirId] = useState('');
  const [status, setStatus] = useState<UserStatus | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const summary = useAdminStatus();
  const users = useAdminUsers(query, choirId);
  const choirs = useAdminChoirs();
  const list = users.data ?? [];
  const shown = status ? list.filter((u) => u.status === status) : list;

  return (
    <div className={`${ui.page} ${styles.page}`}>
      <div className={ui.pageHeader}>
        <div>
          <h1>{t('admin.users.title')}</h1>
          {summary.data && (
            <div className={ui.subtitle}>
              {t('admin.users.summary', { users: summary.data.users, choirs: summary.data.choirs, pending: summary.data.setupPending })}
              {' · '}
              <span data-testid="active-users">{t('admin.users.active', { count: summary.data.activeUsers })}</span>
            </div>
          )}
        </div>
        <button type="button" className={`${ui.button} ${ui.primary}`} onClick={() => setCreating(true)}>
          {t('admin.users.new')}
        </button>
      </div>
      <div className={selected ? styles.withPanel : undefined}>
        <div className={styles.listColumn}>
          <div className={styles.searchRow}>
            <input
              className={ui.input}
              type="search"
              placeholder={t('admin.users.search')}
              aria-label={t('admin.users.search')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <label className={styles.inlineField}>
              <span className={ui.muted}>{t('admin.users.choir')}</span>
              <select className={ui.select} value={choirId} onChange={(e) => setChoirId(e.target.value)}>
                <option value="">{t('admin.users.allChoirs')}</option>
                {(choirs.data ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <FilterChips
            label={t('admin.users.status')}
            value={status}
            onChange={setStatus}
            testId="user-status-filters"
            options={[
              { key: null, label: t('events.all'), count: list.length },
              ...STATUSES.map((s) => ({ key: s, label: t(`admin.status.${s}`), count: list.filter((u) => u.status === s).length })),
            ]}
          />
          <section className={ui.card}>
            {users.isError && <ErrorView onRetry={() => void users.refetch()} />}
            {users.isPending && <p className={ui.muted}>{t('common.loading')}</p>}
            {users.data && (
              <table className={ui.table}>
                <thead>
                  <tr>
                    <th>{t('admin.users.colUser')}</th>
                    <th>{t('admin.users.colChoirs')}</th>
                    <th>{t('admin.users.colStatus')}</th>
                    <th>{t('admin.users.colSignIn')}</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((u) => (
                    <tr key={u.id} className={u.id === selected ? styles.selectedRow : undefined} data-testid="admin-user-row">
                      <td>
                        <Person name={u.displayName} kind={avatarKind(u.memberships.flatMap((m) => m.roles))} size="small">
                          <button type="button" className={styles.nameButton} onClick={() => setSelected(u.id)}>
                            {u.displayName}
                          </button>
                          <span className={ui.secondary}>{u.email}</span>
                        </Person>
                      </td>
                      <td>
                        {u.memberships.length === 0 ? (
                          <span className={ui.muted}>{t('admin.users.noChoir')}</span>
                        ) : (
                          u.memberships.map((m) => <MembershipLine key={m.choirId} membership={m} />)
                        )}
                      </td>
                      <td>
                        <StatusChip user={u} />
                      </td>
                      <td className={`${ui.muted} ${styles.nowrap}`}>{u.lastSignInAt ? relativeTime(u.lastSignInAt, t) : '–'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </div>
        {selected && <UserPanelCard key={selected} userId={selected} onClose={() => setSelected(null)} />}
      </div>
      <NewUserDialog open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}

function MembershipLine({ membership: m }: { membership: AdminMembership }) {
  const { t } = useTranslation();
  const facts = [
    m.choirName,
    ...m.roles.filter((r) => r !== 'SECTION_LEADER').map((r) => t(`roles.${r}`)),
    ...(m.part && !m.roles.includes('DIRECTOR') ? [t(`parts.${m.part}`)] : []),
    ...(m.leadsSection ? [t('admin.users.leads', { part: t(`parts.${m.leadsSection}`) })] : []),
  ];
  return <div>{facts.join(' · ')}</div>;
}

function StatusChip({ user }: { user: AdminUser }) {
  const { t } = useTranslation();
  const days = user.setupLinkExpiresAt ? Math.ceil((new Date(user.setupLinkExpiresAt).getTime() - Date.now()) / DAY_MS) : null;
  const chip = user.status === 'ACTIVE' ? ui.chipSoft : user.status === 'DEACTIVATED' ? ui.chipNeedsWork : ui.chipNeutral;
  return (
    <>
      <span className={`${ui.chip} ${chip}`}>{t(`admin.status.${user.status}`)}</span>
      {user.status === 'SETUP_PENDING' && (
        <span className={ui.secondary}>{days !== null ? t('admin.users.linkValid', { count: Math.max(days, 1) }) : t('admin.users.noLink')}</span>
      )}
    </>
  );
}

/** The user's panel: opening it is written to the audit log. */
function UserPanelCard({ userId, onClose }: { userId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const panel = useUserPanel(userId);
  const changes = useUserChanges(userId);
  const startViewAs = useStartViewAs();
  const choirs = useAdminChoirs();
  const [reason, setReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [link, setLink] = useState<string | null>(null);

  if (panel.isPending) return <aside className={`${ui.card} ${styles.panel}`}>{t('common.loading')}</aside>;
  if (panel.isError || !panel.data) return <aside className={`${ui.card} ${styles.panel}`}><ErrorView onRetry={() => void panel.refetch()} /></aside>;
  const { user, data } = panel.data;
  const first = user.displayName.split(' ')[0] ?? user.displayName;
  const locale = dateLocale();
  const joined = [
    t('admin.panel.joined', { date: formatDate(user.createdAt, locale) }),
    panel.data.joinedVia ? t(`admin.panel.via.${panel.data.joinedVia}`) : null,
    panel.data.passwordSet ? t('admin.panel.passwordSet') : t('admin.panel.noPassword'),
  ].filter(Boolean);
  const otherChoirs = (choirs.data ?? []).filter((c) => !user.memberships.some((m) => m.choirId === c.id));

  return (
    <aside className={`${ui.card} ${styles.panel}`} data-testid="user-panel">
      <div className={styles.panelHeader}>
        <div>
          <h2 className={styles.panelTitle}>{user.displayName}</h2>
          <div className={ui.muted}>{joined.join(' · ')}</div>
        </div>
        <div className={styles.panelHeaderSide}>
          <StatusChip user={user} />
          <button type="button" className={styles.close} aria-label={t('common.close')} onClick={onClose}>
            ×
          </button>
        </div>
      </div>

      <div className={styles.viewAsBox}>
        <label className={ui.field}>
          <span className={ui.label}>{t('admin.panel.reason')}</span>
          <input className={ui.input} value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} />
        </label>
        <button
          type="button"
          className={`${ui.button} ${ui.primary}`}
          disabled={startViewAs.isPending}
          onClick={() => startViewAs.mutate({ userId: user.id, reason }, { onSuccess: () => navigate('/', { replace: true }) })}
        >
          {t('admin.panel.viewAs', { name: first })}
        </button>
        <p className={ui.muted}>{t('admin.panel.viewAsHint', { name: first })}</p>
        {startViewAs.isError && <p className={styles.error}>{apiErrorText(startViewAs.error, t)}</p>}
      </div>

      <AccountForm userId={userId} name={user.displayName} email={user.email} locale={panel.data.locale} />

      <h3 className={styles.sectionLabel}>{t('admin.panel.choirs')}</h3>
      {user.memberships.length === 0 && <p className={ui.muted}>{t('admin.users.noChoir')}</p>}
      {user.memberships.map((m) => (
        <MembershipCard key={m.choirId} userId={userId} membership={m} />
      ))}
      {otherChoirs.length > 0 && <AddToChoir userId={userId} choirs={otherChoirs} />}
      <p className={ui.muted}>{t('admin.panel.choirsHint')}</p>

      <h3 className={styles.sectionLabel}>{t('admin.panel.data')}</h3>
      <p className={styles.noMargin}>
        {t('admin.panel.counts', { submissions: data.submissions, grades: data.grades, comments: data.comments, replies: data.eventReplies })}
      </p>
      <p className={ui.muted}>{t('admin.panel.practiceTakes', { name: first })}</p>

      <h3 className={styles.sectionLabel}>{t('admin.panel.password')}</h3>
      <button
        type="button"
        className={ui.button}
        disabled={changes.passwordLink.isPending}
        onClick={() => changes.passwordLink.mutate(undefined, { onSuccess: (r) => setLink(r.link) })}
      >
        {panel.data.passwordSet ? t('admin.panel.resetLink') : t('admin.panel.setupLink')}
      </button>
      {link && <LinkBox link={link} title={t('admin.panel.linkReady')} hint={t('admin.panel.linkHint', { name: first })} />}

      <h3 className={styles.sectionLabel}>{t('admin.panel.closing')}</h3>
      <div className={styles.buttonRow}>
        <button
          type="button"
          className={`${ui.button} ${styles.dangerOutline}`}
          disabled={changes.deactivate.isPending}
          onClick={() => changes.deactivate.mutate(user.status !== 'DEACTIVATED')}
        >
          {user.status === 'DEACTIVATED' ? t('admin.panel.reactivate') : t('admin.panel.deactivate')}
        </button>
        <button type="button" className={`${ui.button} ${styles.dangerOutline}`} onClick={() => setDeleting(true)}>
          {t('admin.panel.delete')}
        </button>
      </div>
      <p className={ui.muted}>{t('admin.panel.deactivateHint')}</p>
      <p className={styles.logged}>{t('admin.panel.logged', { time: formatDateTime(panel.data.openedAt, locale) })}</p>

      <DeleteUserDialog
        open={deleting}
        name={user.displayName}
        onClose={() => setDeleting(false)}
        pending={changes.remove.isPending}
        error={apiErrorText(changes.remove.error, t)}
        onDelete={(confirmName) =>
          changes.remove.mutate(confirmName, {
            onSuccess: () => {
              setDeleting(false);
              onClose();
            },
          })
        }
      />
    </aside>
  );
}

function AccountForm({ userId, name, email, locale }: { userId: string; name: string; email: string; locale: string }) {
  const { t } = useTranslation();
  const changes = useUserChanges(userId);
  const [values, setValues] = useState({ displayName: name, email, locale });
  function submit(e: FormEvent) {
    e.preventDefault();
    changes.update.mutate(values);
  }
  return (
    <form className={styles.form} onSubmit={submit} noValidate>
      <h3 className={styles.sectionLabel}>{t('admin.panel.account')}</h3>
      <label className={ui.field}>
        <span className={ui.label}>{t('admin.panel.name')}</span>
        <input className={ui.input} value={values.displayName} onChange={(e) => setValues({ ...values, displayName: e.target.value })} />
      </label>
      <label className={ui.field}>
        <span className={ui.label}>{t('admin.panel.email')}</span>
        <input className={ui.input} type="email" value={values.email} onChange={(e) => setValues({ ...values, email: e.target.value })} />
      </label>
      <label className={ui.field}>
        <span className={ui.label}>{t('admin.panel.language')}</span>
        <select className={`${ui.select} ${styles.narrow}`} value={values.locale} onChange={(e) => setValues({ ...values, locale: e.target.value })}>
          {LANGUAGES.map((l) => (
            <option key={l} value={l}>
              {t(`languages.${l}`)}
            </option>
          ))}
        </select>
      </label>
      {changes.update.isError && <p className={styles.error}>{apiErrorText(changes.update.error, t)}</p>}
      <div className={styles.buttonRow}>
        <button type="submit" className={ui.button} disabled={changes.update.isPending}>
          {t('admin.panel.save')}
        </button>
      </div>
    </form>
  );
}

/** A user's part and roles in one choir; the administrator's changes are logged. */
function MembershipCard({ userId, membership: m }: { userId: string; membership: AdminMembership }) {
  const { t } = useTranslation();
  const changes = useUserChanges(userId);
  const [director, setDirector] = useState(m.roles.includes('DIRECTOR'));
  const [chorist, setChorist] = useState(m.roles.includes('CHORIST'));
  const [leader, setLeader] = useState(m.roles.includes('SECTION_LEADER'));
  const [part, setPart] = useState<VoicePart | ''>(m.part ?? '');
  const singer = chorist || leader;
  const valid = (director || singer) && (!singer || part !== '');
  return (
    <div className={styles.membership} data-testid="membership-card">
      <div className={styles.membershipHeader}>
        <strong>{m.choirName}</strong>
        <button type="button" className={`${ui.linkButton} ${styles.dangerLink}`} onClick={() => changes.removeMembership.mutate(m.choirId)}>
          {t('admin.panel.removeFromChoir')}
        </button>
      </div>
      <fieldset className={styles.roles}>
        <legend className={ui.label}>{t('members.colRoles')}</legend>
        <label className={styles.check}>
          <input type="checkbox" checked={director} onChange={(e) => setDirector(e.target.checked)} />
          {t('roles.DIRECTOR')}
        </label>
        <label className={styles.check}>
          <input type="checkbox" checked={chorist} onChange={(e) => setChorist(e.target.checked)} />
          {t('roles.CHORIST')}
        </label>
        <label className={styles.check}>
          <input type="checkbox" checked={leader} onChange={(e) => setLeader(e.target.checked)} />
          {t('roles.SECTION_LEADER')}
        </label>
      </fieldset>
      <div className={styles.buttonRow}>
        <label className={ui.field}>
          <span className={ui.label}>{t('members.colPart')}</span>
          <select className={`${ui.select} ${styles.narrow}`} value={part} onChange={(e) => setPart(e.target.value as VoicePart | '')}>
            <option value="">–</option>
            {VOICE_PARTS.map((p) => (
              <option key={p} value={p}>
                {t(`parts.${p}`)}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className={ui.button}
          disabled={!valid || changes.setMembership.isPending}
          onClick={() =>
            changes.setMembership.mutate({ choirId: m.choirId, part: part || null, director, chorist, sectionLeader: leader })
          }
        >
          {t('admin.panel.save')}
        </button>
      </div>
      {changes.setMembership.isError && <p className={styles.error}>{apiErrorText(changes.setMembership.error, t)}</p>}
    </div>
  );
}

function AddToChoir({ userId, choirs }: { userId: string; choirs: { id: string; name: string }[] }) {
  const { t } = useTranslation();
  const changes = useUserChanges(userId);
  const [choirId, setChoirId] = useState('');
  const [part, setPart] = useState<VoicePart>('S');
  return (
    <div className={styles.buttonRow}>
      <select className={ui.select} value={choirId} aria-label={t('admin.panel.addToChoir')} onChange={(e) => setChoirId(e.target.value)}>
        <option value="">{t('admin.panel.addToChoir')}</option>
        {choirs.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
      <select className={`${ui.select} ${styles.narrow}`} value={part} aria-label={t('members.colPart')} onChange={(e) => setPart(e.target.value as VoicePart)}>
        {VOICE_PARTS.map((p) => (
          <option key={p} value={p}>
            {t(`parts.${p}`)}
          </option>
        ))}
      </select>
      <button
        type="button"
        className={ui.button}
        disabled={!choirId || changes.setMembership.isPending}
        onClick={() =>
          changes.setMembership.mutate(
            { choirId, part, director: false, chorist: true, sectionLeader: false },
            { onSuccess: () => setChoirId('') },
          )
        }
      >
        {t('admin.panel.add')}
      </button>
    </div>
  );
}

/** A link to pass on, shown once. */
export function LinkBox({ link, title, hint }: { link: string; title: string; hint: string }) {
  const { t } = useTranslation();
  return (
    <div className={styles.linkBox} data-testid="link-box">
      <p className={styles.linkTitle}>{title}</p>
      <div className={styles.linkRow}>
        <input className={ui.input} readOnly value={link} aria-label={t('admin.link')} onFocus={(e) => e.target.select()} />
        <CopyButton text={link} label={t('members.inviteDialog.copy')} />
      </div>
      <p className={styles.linkHint}>{hint}</p>
    </div>
  );
}

function DeleteUserDialog({
  open,
  name,
  onClose,
  onDelete,
  pending,
  error,
}: {
  open: boolean;
  name: string;
  onClose: () => void;
  onDelete: (confirmName: string) => void;
  pending: boolean;
  error: string | null;
}) {
  const { t } = useTranslation();
  const [typed, setTyped] = useState('');
  return (
    <Dialog open={open} title={t('admin.deleteUser.title')} onClose={onClose} data-testid="delete-user-dialog">
      <div className={styles.form}>
        <p className={styles.noMargin}>{t('admin.deleteUser.text', { name })}</p>
        <label className={ui.field}>
          <span className={ui.label}>{t('admin.deleteUser.type', { name })}</span>
          <input className={ui.input} value={typed} onChange={(e) => setTyped(e.target.value)} />
        </label>
        {error && <p className={styles.error}>{error}</p>}
        <div className={styles.buttonRow}>
          <button type="button" className={ui.button} onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className={`${ui.button} ${styles.danger}`}
            disabled={typed.trim() !== name.trim() || pending}
            onClick={() => onDelete(typed)}
          >
            {t('admin.deleteUser.confirm')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

function NewUserDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const create = useCreateUser();
  const [values, setValues] = useState({ displayName: '', email: '', locale: 'tr' });
  const [link, setLink] = useState<{ link: string; name: string } | null>(null);
  function close() {
    setValues({ displayName: '', email: '', locale: 'tr' });
    setLink(null);
    create.reset();
    onClose();
  }
  return (
    <Dialog open={open} title={t('admin.newUser.title')} onClose={close} data-testid="new-user-dialog">
      {link ? (
        <div className={styles.form}>
          <LinkBox link={link.link} title={t('admin.newUser.created', { name: link.name })} hint={t('admin.newUser.hint')} />
          <div className={styles.buttonRow}>
            <button type="button" className={`${ui.button} ${ui.primary}`} onClick={close}>
              {t('members.inviteDialog.done')}
            </button>
          </div>
        </div>
      ) : (
        <form
          className={styles.form}
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate(values, { onSuccess: (r) => setLink({ link: r.link ?? '', name: values.displayName }) });
          }}
        >
          <p className={ui.muted}>{t('admin.newUser.intro')}</p>
          <label className={ui.field}>
            <span className={ui.label}>{t('admin.panel.name')}</span>
            <input className={ui.input} value={values.displayName} onChange={(e) => setValues({ ...values, displayName: e.target.value })} />
          </label>
          <label className={ui.field}>
            <span className={ui.label}>{t('admin.panel.email')}</span>
            <input className={ui.input} type="email" value={values.email} onChange={(e) => setValues({ ...values, email: e.target.value })} />
          </label>
          <label className={ui.field}>
            <span className={ui.label}>{t('admin.panel.language')}</span>
            <select className={`${ui.select} ${styles.narrow}`} value={values.locale} onChange={(e) => setValues({ ...values, locale: e.target.value })}>
              {LANGUAGES.map((l) => (
                <option key={l} value={l}>
                  {t(`languages.${l}`)}
                </option>
              ))}
            </select>
          </label>
          {create.isError && <p className={styles.error}>{apiErrorText(create.error, t)}</p>}
          <div className={styles.buttonRow}>
            <button type="button" className={ui.button} onClick={close}>
              {t('common.cancel')}
            </button>
            <button type="submit" className={`${ui.button} ${ui.primary}`} disabled={!values.displayName.trim() || !values.email.trim() || create.isPending}>
              {t('admin.newUser.create')}
            </button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
